import { useEffect, useReducer } from 'react'

import {
  DAY_MS,
  DAYS_CUTOFF,
  HOUR_MS,
  MINUTE_MS,
} from './useFormatRelativeTime'

const computeNextTickDelay = (diffMs: number): number | null => {
  if (diffMs < 0) return null
  if (diffMs < MINUTE_MS) return MINUTE_MS - diffMs
  if (diffMs < HOUR_MS) return MINUTE_MS - (diffMs % MINUTE_MS)
  if (diffMs < DAY_MS) return HOUR_MS - (diffMs % HOUR_MS)
  // Formatter shows "X days ago" through DAYS_CUTOFF inclusive, then short
  // date. Tick until the day after so we don't get stuck at "7 days ago".
  if (diffMs < (DAYS_CUTOFF + 1) * DAY_MS) return DAY_MS - (diffMs % DAY_MS)
  return null
}

/**
 * Schedules a re-render at the next relative-time band boundary
 * ("just now" → "1 minute ago" → ... → ">7 days short date").
 * Pass `null` to disable.
 */
export const useRelativeTimeTick = (date: Nullable<Date>) => {
  const [tick, forceTick] = useReducer((c: number) => c + 1, 0)

  useEffect(() => {
    if (date === null) return undefined

    const delay = computeNextTickDelay(Date.now() - date.getTime())
    if (delay === null) return undefined

    const timeoutId = window.setTimeout(forceTick, Math.max(delay, 1))
    return () => window.clearTimeout(timeoutId)
  }, [date, tick])
}
